import { useEffect, useState } from "react";

/**
 * Rotating example searches.
 *
 * The lookup tools used to show a single Las Vegas example, which made the
 * coverage look narrower than it is. These lists cycle through counties we
 * actually cover so a visitor sees their part of the country represented.
 */

/**
 * Address examples for the address-driven easement lookup. Every address here
 * was run end to end and returns recorded strips from that county's own map.
 */
export const ADDRESS_SEARCH_EXAMPLES = [
  "233 S Wacker Dr, Chicago, IL",
  "301 W 2nd St, Austin, TX",
  "90 W Broad St, Columbus, OH",
] as const;

/** County / city examples for the county recording-rules lookup. */
export const COUNTY_SEARCH_EXAMPLES = [
  "Chicago, Cook County, Illinois…",
  "Austin, Travis County, Texas…",
  "Atlanta, Fulton County, Georgia…",
  "Columbus, Franklin County, Ohio…",
  "Phoenix, Maricopa County, Arizona…",
  "Seattle, King County, Washington…",
] as const;

/** Short one-click suggestions shown under the county search box. */
export const COUNTY_SEARCH_CHIPS = [
  "Cook County",
  "Travis County",
  "Fulton County",
  "Franklin County",
  "Maricopa County",
] as const;

/**
 * Cycles through `examples`, starting on the first one so the server render
 * and the first client render always agree.
 */
export function useRotatingExample(
  examples: readonly string[],
  intervalMs = 3500,
): string {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (examples.length < 2) return;
    const media =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;
    if (media?.matches) return;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % examples.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [examples, intervalMs]);

  return examples[index] ?? examples[0] ?? "";
}
